import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasCategoryContext;

export interface CategoryDetailLinkListStyles {
    container?: GridContainerProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    Clptitle?: InjectableCss;
}

export const categoryDetailLinkListStyles: CategoryDetailLinkListStyles = {
    container: {
        gap: 0,
        css: css`
            margin-top: 51px;
            padding-right: 32px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-top: 33px;
                            padding-right: 0px;
                            padding-left: 0;
                            padding: 0 50px;
                            margin-bottom: 24px;
                        `,
                        css`
                            margin-top: 33px;
                            padding-right: 0px;
                            padding-left: 0;
                            padding: 0 50px;
                            margin-bottom: 24px;
                        `,
                        css`
                            margin-top: 33px;
                            padding-right: 0px;
                            padding-left: 0;
                            padding: 0 50px;
                            margin-bottom: 24px;
                        `,
                        css`
                            margin-top: 51px;
                            padding-right: 32px;
                            margin-bottom: 0px;
                            padding-left: 0;
                        `,
                        css`
                            margin-top: 51px;
                            padding-right: 32px;
                            padding-left: 0;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    subCategoryNameLinkItem: {
        width: 12,
        css: css`
            span {
                color: #333333 !important;
                font-size: 13px;
                letter-spacing: 1.08px;
                line-height: 30px;
                :hover {
                    color: #006bc0 !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                font-size: 16px;
                                letter-spacing: 1.33px;
                                line-height: 40px;
                            `,
                            css`
                                font-size: 16px;
                                letter-spacing: 1.33px;
                                line-height: 40px;
                            `,
                            css`
                                font-size: 16px;
                                letter-spacing: 1.33px;
                                line-height: 40px;
                            `,
                            css`
                                color: #333333 !important;
                                font-size: 13px;
                                letter-spacing: 1.08px;
                                line-height: 30px;
                            `,
                            css`
                                color: #333333 !important;
                                font-size: 13px;
                                letter-spacing: 1.08px;
                                line-height: 30px;
                            `,
                        ],
                        "min",
                    )}
            }
        `,
    },
    Clptitle: {
        css: css`
            font-size: 21px;
            font-weight: bold;
            letter-spacing: 1.75px;
            line-height: 25px;
            color: #33333a;
            margin-left: 7px;
            text-transform: uppercase;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 21px;
                            font-weight: bold;
                            letter-spacing: 0;
                            line-height: 25px;
                        `,
                        css`
                            font-size: 21px;
                            font-weight: bold;
                            letter-spacing: 0;
                            line-height: 25px;
                        `,
                        css`
                            font-size: 21px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                        css`
                            font-size: 14px;
                            font-weight: bold;
                            letter-spacing: 1.75px;
                            line-height: 25px;
                            color: #33333a;
                            margin-left: 7px;
                        `,
                    ],
                    "minMax",
                )}
        `,
    },
};

const styles = categoryDetailLinkListStyles;

const CategoryDetailLinkList = ({ category }: Props) => {
    if (!category || !category.subCategoryIds || category.subCategoryIds.length === 0) {
        return null;
    }

    return (
        <GridContainer {...styles.container}>
            <StyledWrapper
                css={css`
                    border-bottom: 2px solid rgba(0, 0, 0, 0.1);
                    margin-bottom: 16px;
                    width: 100%;
                `}
            >
                <StyledWrapper
                    css={css`
                        display: flex;
                        margin-bottom: 19px;
                    `}
                >
                    <StyledWrapper
                        css={css`
                            display: flex;
                        `}
                    >
                        <LazyImage
                            src="/UserFiles/Images/MiniLogo.jpg"
                            css={css`
                                width: 20px;
                            `}
                        />
                    </StyledWrapper>
                    <StyledWrapper>
                        <Typography as="h3" {...styles.Clptitle}>
                            {category.shortDescription}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {category.subCategoryIds.map(subCategoryId => (
                <GridItem key={subCategoryId.toString()} {...styles.subCategoryNameLinkItem}>
                    <SubCategoryLink subCategoryId={subCategoryId} />
                </GridItem>
            ))}
        </GridContainer>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: { subCategoryId: string }) => {
    return {
        category: getCategoryState(state, ownProps.subCategoryId).value,
    };
};

const SubCategoryLinkView = ({ category }: ReturnType<typeof mapStateToProps>) => {
    if (!category) {
        return null;
    }

    return (
        <Link
            href={category.path}
            {...styles.subCategoryNameLink}
            data-test-selector={`categoryDetailsLinkListLink_${category.id}`}
        >
            {category.shortDescription}
        </Link>
    );
};

const SubCategoryLink = connect(mapStateToProps)(SubCategoryLinkView);

const widgetModule: WidgetModule = {
    component: withCategory(CategoryDetailLinkList),
    definition: {
        group: "Category Details",
        icon: "list-ul",
        allowedContexts: ["CategoryDetailsPage"],
    },
};

export default widgetModule;
